import { useCallback, useEffect, useRef } from 'react';
import { npcPortrait } from '@/lib/assets';
import { NPCS } from '@/data/npcs';
import { TEXTURES } from '@/lib/assets';
import type { NpcId } from '@/lib/assets';
import type { QuestChoice } from '@/types/quests';

interface DialogueModalProps {
  npc: NpcId;
  text: string;
  choices?: QuestChoice[];
  continueText?: string;
  onChoice: (choice: QuestChoice) => void;
  onContinue: () => void;
  onClose: () => void;
}

export function DialogueModal({
  npc,
  text,
  choices,
  continueText,
  onChoice,
  onContinue,
  onClose,
}: DialogueModalProps): JSX.Element {
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstButtonRef = useRef<HTMLButtonElement>(null);

  const npcMeta = NPCS[npc];
  const portrait = npcPortrait(npc);
  const npcName = npcMeta?.name ?? npc;

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      // Focus trap inside the modal.
      if (e.key === 'Tab') {
        const dialog = dialogRef.current;
        if (!dialog) return;
        const focusable = dialog.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length === 0) return;
        const first = focusable[0]!;
        const last = focusable[focusable.length - 1]!;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    // Focus the first button when the modal opens.
    const timer = setTimeout(() => firstButtonRef.current?.focus(), 50);
    // Prevent body scroll.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      clearTimeout(timer);
      document.body.style.overflow = prev;
    };
  }, [handleKeyDown]);

  const paragraphs = text.split('\n\n').filter(Boolean);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-3 py-6 sm:px-6"
      role="dialog"
      aria-modal="true"
      aria-label={`${npcName} speaks`}
    >
      {/* Backdrop */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- Escape key handled by the dialog's keydown listener */}
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />

      {/* Dialogue card */}
      <div
        ref={dialogRef}
        className="relative w-full max-w-2xl max-h-[90dvh] overflow-y-auto rounded-lg border-2 border-wood-mid/70 shadow-[0_8px_40px_rgba(0,0,0,0.7)] stage-fade-in"
        style={{
          backgroundImage: `url("${TEXTURES.parchment}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-parchment/85 pointer-events-none rounded-lg" />

        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 inline-flex h-8 w-8 items-center justify-center rounded-full border border-wood-mid/40 bg-parchment-light/80 text-ink-mid hover:text-ink hover:border-wood-mid transition-colors text-sm"
          aria-label="Close dialogue"
        >
          &times;
        </button>

        <div className="relative px-5 sm:px-8 pt-6 pb-6">
          {/* NPC header */}
          <div className="flex items-center gap-4 mb-5">
            <div className="shrink-0 h-16 w-16 sm:h-24 sm:w-24 rounded-full overflow-hidden border-2 border-candle/50 shadow-[0_0_12px_rgba(232,177,75,0.2)]">
              <img
                src={portrait}
                alt={npcName}
                className="h-full w-full object-cover"
                width={96}
                height={96}
              />
            </div>
            <div>
              <h2 className="font-display text-xl sm:text-2xl text-wood-dark">{npcName}</h2>
              {npcMeta?.title && <p className="text-xs text-ink-mid italic">{npcMeta.title}</p>}
            </div>
          </div>

          {/* Dialogue text */}
          <div className="space-y-3 text-ink text-base sm:text-lg leading-relaxed mb-6">
            {paragraphs.map((p, i) => (
              <p key={i} className="stage-fade-in" style={{ animationDelay: `${i * 0.1}s` }}>
                &ldquo;{p}&rdquo;
              </p>
            ))}
          </div>

          {/* Choices or continue */}
          <div className="space-y-2.5">
            {choices && choices.length > 0 ? (
              choices.map((choice, i) => (
                <button
                  key={choice.label}
                  ref={i === 0 ? firstButtonRef : undefined}
                  type="button"
                  onClick={() => onChoice(choice)}
                  className="w-full text-left rounded-md border-2 border-wood-mid/40 bg-parchment-light/60 px-4 py-3 text-sm sm:text-base text-ink hover:border-candle hover:bg-candle/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-candle transition-colors min-h-11"
                >
                  {choice.label}
                </button>
              ))
            ) : (
              <button
                ref={firstButtonRef}
                type="button"
                onClick={onContinue}
                className="w-full inline-flex items-center justify-center rounded-md bg-sea-deep text-candle border-2 border-candle/50 px-5 py-3 font-display tracking-[0.2em] uppercase text-sm shadow-[0_2px_8px_rgba(0,0,0,0.4)] hover:bg-sea-dark hover:border-candle hover:shadow-[0_2px_12px_rgba(232,177,75,0.2)] transition-all duration-200 min-h-12 focus:outline-none focus-visible:ring-2 focus-visible:ring-candle"
              >
                {continueText ?? 'Continue'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
